import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Users, Send, ArrowRight, User, Clock, Check, CheckCheck, 
  Plus, RefreshCw, Search, Shield
} from 'lucide-react';
import Breadcrumb from '../components/Breadcrumb';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminSupport() {
  const navigate = useNavigate();
  const { user: currentUser, getAuthHeaders } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [admins, setAdmins] = useState([]);
  const [showNewChat, setShowNewChat] = useState(false);
  
  const messagesEndRef = useRef(null);
  const headers = getAuthHeaders();

  const fetchAdmins = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/whatsapp/admins`, { headers });
      // Filter out current user
      const otherAdmins = (res.data.data || []).filter(a => a.email !== currentUser?.email);
      setAdmins(otherAdmins);
    } catch (err) {
      console.log('Admins fetch:', err.message);
    }
  };

  const fetchConversations = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin-chat/conversations`, { headers });
      setConversations(res.data.data || []);
    } catch (err) {
      // API might not exist yet, use admins list
      console.log('Using admins as conversations');
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async (adminId) => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin-chat/messages/${adminId}`, { headers });
      setMessages(res.data.data || []);
    } catch (err) {
      setMessages([]);
    }
  };

  useEffect(() => {
    fetchAdmins();
    fetchConversations();
    const interval = setInterval(fetchConversations, 5000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedAdmin) {
      fetchMessages(selectedAdmin.id);
      const msgInterval = setInterval(() => fetchMessages(selectedAdmin.id), 3000);
      return () => clearInterval(msgInterval);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAdmin]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedAdmin) return;

    setSending(true);
    try {
      await axios.post(
        `${BACKEND_URL}/api/admin-chat/send`,
        { to_admin_id: selectedAdmin.id, message: newMessage },
        { headers }
      );
      setNewMessage('');
      fetchMessages(selectedAdmin.id);
      toast.success('تم إرسال الرسالة');
    } catch (err) {
      // Fallback - store locally
      const newMsg = {
        id: Date.now().toString(),
        from: currentUser.email,
        to: selectedAdmin.email,
        text: newMessage,
        direction: 'outgoing',
        created_at: new Date().toISOString()
      };
      setMessages(prev => [...prev, newMsg]);
      setNewMessage('');
      toast.info('تم حفظ الرسالة محلياً');
    } finally {
      setSending(false);
    }
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    return date.toLocaleTimeString('ar-SA', { hour: '2-digit', minute: '2-digit' });
  };

  const filteredAdmins = admins.filter(a => 
    a.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    a.email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      {/* Breadcrumb */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4">
        <Breadcrumb 
          pathAr="لوحة التحكم / الدعم والمساعدة / دعم المشرفين" 
          pathEn="Dashboard / Support / Admin Support" 
        />
      </div>
      
      {/* Header */}
      <section className="border-b border-[#1a1a2e] py-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                onClick={() => navigate('/admin-dashboard')}
                className="text-[#808080] hover:text-white hover:bg-[#1a1a2e] -ms-3"
                data-testid="back-btn"
              >
                <ArrowRight className="h-4 w-4 me-2 rotate-180" />
                العودة
              </Button>
              <Users className="h-8 w-8 text-sky-500" />
              <div>
                <h1 className="text-xl font-bold text-white">دعم الأدمين</h1>
                <p className="text-sm text-[#808080]">التواصل الداخلي بين المشرفين</p>
              </div>
            </div>
            
            <Button
              onClick={fetchConversations}
              variant="outline"
              className="border-[#2a2a4e] text-[#808080] hover:text-white hover:bg-[#1a1a2e]"
            >
              <RefreshCw className="h-4 w-4 me-2" />
              تحديث
            </Button>
          </div>
        </div>
      </section>

      {/* Chat Container */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <div className="bg-[#1a1a2e] rounded-3xl border border-[#2a2a4e] overflow-hidden h-[calc(100vh-180px)] flex">
          
          {/* Admins List */}
          <div className="w-[350px] border-e border-[#2a2a4e] flex flex-col">
            {/* Search */}
            <div className="p-4 border-b border-[#2a2a4e]">
              <div className="relative">
                <Search className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#606060]" />
                <Input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="بحث عن مشرف..."
                  className="bg-[#0a0a0a] border-[#2a2a4e] text-white placeholder:text-[#606060] pr-10 rounded-xl"
                />
              </div>
            </div>
            
            {/* Admin List */}
            <div className="flex-1 overflow-y-auto">
              {loading ? (
                <div className="p-4 space-y-3">
                  {[1,2,3].map(i => (
                    <div key={i} className="h-16 bg-[#0a0a0a] rounded-xl animate-pulse" />
                  ))}
                </div>
              ) : filteredAdmins.length === 0 ? (
                <div className="p-8 text-center text-[#808080]">
                  <Users className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p>لا يوجد مشرفين آخرين</p>
                </div>
              ) : (
                filteredAdmins.map((admin) => (
                  <button
                    key={admin.id || admin.email}
                    onClick={() => setSelectedAdmin(admin)}
                    className={`w-full p-4 flex items-center gap-3 hover:bg-[#2a2a4e] transition-colors text-right border-b border-[#2a2a4e]/50 ${
                      selectedAdmin?.email === admin.email ? 'bg-[#2a2a4e]' : ''
                    }`}
                  >
                    <div className="w-12 h-12 rounded-full bg-sky-500/20 flex items-center justify-center flex-shrink-0">
                      <Shield className="h-6 w-6 text-sky-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-white truncate">{admin.name}</p>
                      <p className="text-sm text-[#808080] truncate">{admin.email}</p>
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>

          {/* Chat Area */}
          <div className="flex-1 flex flex-col">
            {selectedAdmin ? (
              <>
                {/* Chat Header */}
                <div className="p-4 border-b border-[#2a2a4e] flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-sky-500/20 flex items-center justify-center">
                    <Shield className="h-5 w-5 text-sky-500" />
                  </div>
                  <div>
                    <p className="font-semibold text-white">{selectedAdmin.name}</p>
                    <p className="text-xs text-[#808080]">{selectedAdmin.email}</p>
                  </div>
                </div>

                {/* Messages */}
                <div className="flex-1 overflow-y-auto p-4 space-y-3">
                  {messages.length === 0 ? (
                    <div className="flex items-center justify-center h-full text-[#808080]">
                      <div className="text-center">
                        <Users className="h-12 w-12 mx-auto mb-3 opacity-30" />
                        <p>ابدأ المحادثة مع {selectedAdmin.name}</p>
                      </div>
                    </div>
                  ) : (
                    messages.map((msg) => (
                      <div
                        key={msg.id}
                        className={`flex ${msg.direction === 'outgoing' || msg.from === currentUser.email ? 'justify-start' : 'justify-end'}`}
                      >
                        <div
                          className={`max-w-[70%] p-3 rounded-2xl ${
                            msg.direction === 'outgoing' || msg.from === currentUser.email
                              ? 'bg-sky-500 text-white rounded-bl-sm'
                              : 'bg-[#2a2a4e] text-white rounded-br-sm'
                          }`}
                        >
                          <p className="text-sm whitespace-pre-wrap">{msg.text}</p>
                          <div className="flex items-center justify-end gap-1 mt-1">
                            <span className="text-[10px] opacity-70">{formatTime(msg.created_at)}</span>
                            {(msg.direction === 'outgoing' || msg.from === currentUser.email) && (
                              <CheckCheck className="h-3 w-3 opacity-70" />
                            )}
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                  <div ref={messagesEndRef} />
                </div>

                {/* Message Input */}
                <form onSubmit={sendMessage} className="p-4 border-t border-[#2a2a4e]">
                  <div className="flex gap-3">
                    <Input
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      placeholder="اكتب رسالتك..."
                      className="flex-1 bg-[#0a0a0a] border-[#2a2a4e] text-white placeholder:text-[#606060] rounded-xl"
                      disabled={sending}
                    />
                    <Button
                      type="submit"
                      disabled={!newMessage.trim() || sending}
                      className="bg-sky-500 hover:bg-sky-600 text-white px-6 rounded-xl"
                    >
                      <Send className="h-5 w-5" />
                    </Button>
                  </div>
                </form>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center text-[#808080]">
                <div className="text-center">
                  <Users className="h-20 w-20 mx-auto mb-4 opacity-20 text-sky-500" />
                  <p className="text-xl font-medium">دعم الأدمين</p>
                  <p className="text-sm mt-2 max-w-sm mx-auto">
                    اختر مشرف من القائمة للتواصل معه
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
